import { TooltipProvider } from '@lastrouter/ui';
import { keepPreviousData, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { ApiError } from '../api/client.js';
import { AppearancePage } from '../pages/appearance.js';
import { AssetPage } from '../pages/assets/index.js';
import { ClientPage, type ClientTab } from '../pages/clients/client.js';
import { ClientsPage } from '../pages/clients/index.js';
import { ASSET_TABS, isOldAssetTab, legacyAssetTab, parseAppsSearch, type AppsSearch, type AssetTab } from '../pages/assets/logic.js';
import { AuditPage } from '../pages/audit.js';
import { ConnectionsPage } from '../pages/connections.js';
import { ConversationsPage } from '../pages/conversations.js';
import { DraftEditorPage } from '../pages/context/draft-editor.js';
import { ContextPage, type ContextTab } from '../pages/context/index.js';
import { LoginPage } from '../pages/login.js';
import { OverviewPage } from '../pages/overview.js';
import { QualityPage, type QualityTab } from '../pages/quality.js';
import { SystemPage } from '../pages/system.js';
import { ChatProductPage, McpProductPage, TeamsProductPage } from '../pages/products.js';
import { UsersPage, type PeopleTab } from '../pages/users.js';
import { AppShell } from './shell.js';

const CONTEXT_TABS: ContextTab[] = ['published', 'drafts', 'history'];
const QUALITY_TABS: QualityTab[] = ['weekly', 'feedback', 'proposals', 'candidates', 'reports'];
const CLIENT_TABS: ClientTab[] = ['apps', 'context'];
const PEOPLE_TABS: PeopleTab[] = ['users', 'limits', 'simulate'];

/** Só aceita na URL os valores conhecidos: o resto some, e a tela abre na aba padrão. */
function tabSearch<T extends string>(allowed: readonly T[]) {
  return (s: Record<string, unknown>): { tab?: T } => (allowed.includes(s.tab as T) ? { tab: s.tab as T } : {});
}

/** Aba e estado da lista de apps (busca, filtros, ordem, página) na mesma URL. */
function tabAndAppsSearch<T extends string>(allowed: readonly T[]) {
  return (s: Record<string, unknown>): { tab?: T } & AppsSearch => ({ ...tabSearch(allowed)(s), ...parseAppsSearch(s) });
}

/** Tira da URL o que ficou vazio: o endereço mostra só o que está valendo. */
const compact = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as T;

/*
 * Endereços antigos, em português, de antes de 28/10/2026: cada um leva ao novo com os parâmetros traduzidos
 * (aba -> tab, parte -> tab e os valores). Sai numa versão futura, quando os links salvos já tiverem mudado.
 */
export const LEGACY_PATHS: [string, string][] = [
  ['/entrar', '/login'],
  ['/conexoes', '/connections'],
  ['/clientes', '/clients'],
  ['/clientes/$id', '/clients/$id'],
  ['/contexto', '/context'],
  ['/contexto/rascunhos/$draftId', '/context/drafts/$draftId'],
  ['/usuarios', '/users'],
  ['/acesso', '/users?tab=simulate'],
  ['/limites', '/users?tab=limits'],
  ['/qualidade', '/quality'],
  ['/canais', '/products/mcp'],
  ['/conversas', '/conversations'],
  ['/auditoria', '/audit'],
  ['/aparencia', '/appearance'],
  ['/configuracoes', '/settings'],
  // Canais, limites e acesso viraram partes de Produtos e Pessoas (menu de 28/10/2026).
  ['/channels', '/products/mcp'],
  ['/limits', '/users?tab=limits'],
  ['/access', '/users?tab=simulate'],
  // Exportações saíram do menu (29/10/2026): os arquivos ficam no Resumo de cada app.
  ['/products/exports', '/clients'],
];
const LEGACY_TAB: Record<string, string> = {
  publicado: 'published',
  rascunhos: 'drafts',
  historico: 'history',
  semanal: 'weekly',
  propostas: 'proposals',
  candidatas: 'candidates',
  relatorios: 'reports',
  contexto: 'context',
  feedback: 'feedback',
  apps: 'apps',
};

/** Endereço novo para um antigo: caminho com os parâmetros e a busca traduzida. */
export function legacyHref(to: string, params: Record<string, string>, search: Record<string, unknown>): string {
  const path = to.replace(/\$(\w+)/g, (_m, k: string) => encodeURIComponent(params[k] ?? ''));
  const tab = typeof search.aba === 'string' ? LEGACY_TAB[search.aba] : undefined;
  return tab ? `${path}?tab=${encodeURIComponent(tab)}` : path;
}

type AssetSearch = { tab?: string; step?: string; parte?: string };

/**
 * Endereço novo da página do app quando a URL é de antes das abas (seção, `generate&step=`, `history`, `parte=`);
 * null quando ela já está certa.
 */
export function assetRedirect(id: string, s: AssetSearch): string | null {
  if (s.parte === undefined && s.step === undefined && (s.tab === undefined || ASSET_TABS.includes(s.tab as AssetTab))) return null;
  const tab = legacyAssetTab(s);
  return `/apps/${encodeURIComponent(id)}${tab ? `?tab=${tab}` : ''}`;
}

function Root() {
  return (
    <TooltipProvider>
      <Outlet />
      <Toaster
        position="bottom-right"
        closeButton
        toastOptions={{ style: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', fontFamily: 'var(--lr-font-sans)' } }}
      />
    </TooltipProvider>
  );
}

const rootRoute = createRootRoute({ component: Root });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppShell });

const legacyRoutes = LEGACY_PATHS.map(([from, to]) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: from,
    beforeLoad: ({ params, search }) => {
      throw redirect({ href: legacyHref(to, params as Record<string, string>, search as Record<string, unknown>), replace: true });
    },
  }),
);

const overviewRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: OverviewPage });
const connectionsRoute = createRoute({ getParentRoute: () => appRoute, path: '/connections', component: ConnectionsPage });

const contextRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/context',
  validateSearch: tabSearch(CONTEXT_TABS),
  component: function ContextRoute() {
    const { tab } = contextRoute.useSearch();
    const navigate = useNavigate({ from: '/context' });
    return <ContextPage tab={tab ?? 'published'} onTab={(t) => void navigate({ search: { tab: t } })} />;
  },
});

const draftRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/context/drafts/$draftId',
  component: function DraftRoute() {
    const { draftId } = draftRoute.useParams();
    return <DraftEditorPage draftId={draftId} />;
  },
});

const clientsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients',
  validateSearch: parseAppsSearch,
  component: function ClientsRoute() {
    const search = clientsRoute.useSearch();
    const navigate = useNavigate({ from: '/clients' });
    return (
      <ClientsPage
        search={search}
        setSearch={(patch, replace) => void navigate({ search: (prev) => compact({ ...prev, ...patch }), replace: replace ?? false })}
      />
    );
  },
});

const clientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients/$id',
  validateSearch: tabAndAppsSearch(CLIENT_TABS),
  component: function ClientRoute() {
    const { id } = clientRoute.useParams();
    const { tab, ...search } = clientRoute.useSearch();
    const navigate = useNavigate({ from: '/clients/$id' });
    return (
      <ClientPage
        id={id}
        tab={tab ?? 'apps'}
        onTab={(t) => void navigate({ search: { tab: t } })}
        search={search}
        setSearch={(patch, replace) => void navigate({ search: (prev) => compact({ ...prev, ...patch }), replace: replace ?? false })}
      />
    );
  },
});

const assetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/apps/$id',
  // Aba na URL (`tab`); os valores antigos e `step`/`parte` ficam só para o redirecionamento.
  validateSearch: (s: Record<string, unknown>): AssetSearch => ({
    ...(typeof s.tab === 'string' && (ASSET_TABS.includes(s.tab as AssetTab) || isOldAssetTab(s.tab)) ? { tab: s.tab } : {}),
    ...(s.tab === 'generate' && typeof s.step === 'string' ? { step: s.step } : {}),
    ...(typeof s.parte === 'string' ? { parte: s.parte } : {}),
  }),
  beforeLoad: ({ params, search }) => {
    const to = assetRedirect(params.id, search);
    if (to) throw redirect({ href: to, replace: true });
  },
  component: function AssetRoute() {
    const { id } = assetRoute.useParams();
    const { tab } = assetRoute.useSearch();
    const navigate = useNavigate({ from: '/apps/$id' });
    return <AssetPage id={id} tab={ASSET_TABS.includes(tab as AssetTab) ? (tab as AssetTab) : undefined} onTab={(t) => void navigate({ search: { tab: t } })} />;
  },
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  validateSearch: tabSearch(PEOPLE_TABS),
  component: function UsersRoute() {
    const { tab } = usersRoute.useSearch();
    const navigate = useNavigate({ from: '/users' });
    return <UsersPage tab={tab ?? 'users'} onTab={(t) => void navigate({ search: t === 'users' ? {} : { tab: t } })} />;
  },
});

const chatProductRoute = createRoute({ getParentRoute: () => appRoute, path: '/products/chat', component: ChatProductPage });
const teamsProductRoute = createRoute({ getParentRoute: () => appRoute, path: '/products/teams', component: TeamsProductPage });
const mcpProductRoute = createRoute({ getParentRoute: () => appRoute, path: '/products/mcp', component: McpProductPage });

const qualityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality',
  validateSearch: tabSearch(QUALITY_TABS),
  component: function QualityRoute() {
    const { tab } = qualityRoute.useSearch();
    const navigate = useNavigate({ from: '/quality' });
    return <QualityPage tab={tab ?? 'weekly'} onTab={(t) => void navigate({ search: { tab: t } })} />;
  },
});

const conversationsRoute = createRoute({ getParentRoute: () => appRoute, path: '/conversations', component: ConversationsPage });
const auditRoute = createRoute({ getParentRoute: () => appRoute, path: '/audit', component: AuditPage });
const appearanceRoute = createRoute({ getParentRoute: () => appRoute, path: '/appearance', component: AppearancePage });
const systemRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SystemPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  ...legacyRoutes,
  appRoute.addChildren([
    overviewRoute,
    connectionsRoute,
    contextRoute,
    draftRoute,
    clientsRoute,
    clientRoute,
    assetRoute,
    usersRoute,
    chatProductRoute,
    teamsProductRoute,
    mcpProductRoute,
    qualityRoute,
    conversationsRoute,
    auditRoute,
    appearanceRoute,
    systemRoute,
  ]),
]);

export function createAppRouter() {
  return createRouter({ routeTree, defaultPreload: false });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

/** Sessão que expira no meio do uso leva de volta à tela de entrar. */
export function createQueryClient(onUnauthorized: () => void): QueryClient {
  const handle = (e: unknown, key?: readonly unknown[]) => {
    if (e instanceof ApiError && e.status === 401 && key?.[0] !== 'session' && key?.[0] !== 'publicAppearance') onUnauthorized();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: (e, q) => handle(e, q.queryKey) }),
    mutationCache: new MutationCache({ onError: (e) => handle(e) }),
    defaultOptions: {
      queries: {
        // Ao trocar filtro, busca ou página, a tela segue com o dado anterior (esmaecido pelo QueryState) em vez de piscar o esqueleto.
        placeholderData: keepPreviousData,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
      },
    },
  });
}
